import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  createCloseInstruction,
  createOpenInstruction,
  isManualTradingConfigured,
  listInstructionsIfIdle,
  listPositionGroupSettings,
  listHistoryOrders,
  submitSingleLegTask,
  updatePositionGroupSetting,
  type AccountInfoView,
  type DailyAccountPnlView,
  type InstructionValidityView,
  type InstructionView,
  type OpenInstructionBody,
  type PositionGroupView,
  type PositionInGroupView,
  type FundingRecordView,
  type HistoryOrderView,
  type SubmitSingleLegTaskBody,
  type SyncedPositionsView,
} from '@/services/manualTradingApi';
import {
  INSTRUCTIONS_CHANGED_EVENT,
  notifyInstructionsChanged,
} from '@/lib/instructionThresholdLines';
import { PairHistoryTasksPanel } from '@/components/ManualTradingPanel/PairHistoryTasksPanel';
import {
  activeInstructionChipTitle,
  formatActiveInstructionChipText,
  indexActiveInstructionsByGroupKey,
  positionGroupInstructionKey,
} from '@/lib/instructionDisplay';
import { createSerialLoader } from '@/lib/instructionPolling';
import { useAuth } from '@/auth/AuthContext';
import {
  confirmOpenThresholdIfNeeded,
  resolveSpreadDirectionForLegs,
  type LatestOpenLineByDirection,
} from '@/lib/openLineThresholdConfirm';
import { normalizeSymbolInput } from '@/lib/exchangeInstId';
import { usePortfolioWs } from '@/hooks/usePortfolioWs';

const INSTRUCTION_POLL_MS = 10_000;

function activeInstructionsForGroup(
  g: PositionGroupView,
  index: Map<string, InstructionView[]>,
): InstructionView[] {
  const hp = hedgeParamsFromGroup(g);
  if ('error' in hp) return [];
  const key = positionGroupInstructionKey(hp.symbol, hp.long_exchange, hp.short_exchange);
  return index.get(key) ?? [];
}

function PositionGroupActiveInstructions({
  instructions,
  onSelectInstruction,
}: {
  instructions: InstructionView[];
  onSelectInstruction?: (row: InstructionView) => void;
}) {
  if (instructions.length === 0) return null;

  return (
    <div
      className="realtime-spread-pos-group-instructions"
      onClick={(ev) => ev.stopPropagation()}
      onKeyDown={(ev) => ev.stopPropagation()}
    >
      <span className="realtime-spread-pos-group-instructions-label">活跃指令</span>
      <ul className="realtime-spread-pos-group-instruction-list">
        {instructions.map((row) => (
          <li key={row.instruction_id}>
            <button
              type="button"
              className={`realtime-spread-pos-instruction-row${row.status === 'Paused' ? ' is-paused' : ''}`}
              title={`${activeInstructionChipTitle(row)}\n点击查看执行记录`}
              onClick={() => onSelectInstruction?.(row)}
            >
              <span className="realtime-spread-pos-instruction-row-kind">
                {row.action === 'Open' ? '开仓' : '关仓'}
              </span>
              <span className="realtime-spread-pos-instruction-row-text">
                {formatActiveInstructionChipText(row)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function fmtTsMs(ms: number): string {
  if (!ms || !Number.isFinite(ms)) return '—';
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19);
}

function InstructionExecutionDialog({
  instruction,
  onClose,
}: {
  instruction: InstructionView;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<HistoryOrderView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listHistoryOrders({
      symbol: normalizeSymbolInput(instruction.symbol),
      long_exchange: instruction.long_exchange,
      short_exchange: instruction.short_exchange,
      instruction_id: instruction.instruction_id,
      limit: 500,
    })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instruction]);

  const title =
    instruction.action === 'Open'
      ? `开仓指令执行记录 · ${instruction.symbol}`
      : `关仓指令执行记录 · ${instruction.symbol}`;

  return (
    <div
      className="realtime-spread-pos-modal-overlay realtime-spread-pos-instruction-records-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="realtime-spread-pos-modal realtime-spread-pos-instruction-records-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="instruction-records-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="realtime-spread-pos-modal-head">
          <div>
            <h2 id="instruction-records-title">{title}</h2>
            <p className="realtime-spread-pos-modal-sub mono small">
              {instruction.instruction_id} · Long {instruction.long_exchange} / Short{' '}
              {instruction.short_exchange}
            </p>
          </div>
          <button type="button" className="realtime-spread-pos-modal-close" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="realtime-spread-pos-modal-body">
          {loading ? <p className="realtime-spread-positions-hint">加载执行记录…</p> : null}
          {error ? <div className="realtime-spread-positions-error">{error}</div> : null}
          {!loading && !error && rows.length === 0 ? (
            <p className="realtime-spread-positions-hint">暂无执行记录（指令可能尚未触发，或记录尚未归档）。</p>
          ) : null}
          {rows.length > 0 ? (
            <div className="realtime-spread-positions-table-wrap">
              <table className="realtime-spread-positions-table realtime-spread-pos-instruction-records-table">
                <thead>
                  <tr>
                    <th>时间</th>
                    <th>Task</th>
                    <th>交易所</th>
                    <th>合约</th>
                    <th>方向</th>
                    <th className="num">名义 USD</th>
                    <th className="num">成交价</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.task_id}-${row.client_order_id}-${row.exchange}`}>
                      <td className="mono small">{fmtTsMs(row.updated_at_ms)}</td>
                      <td className="mono small" title={row.task_id}>
                        {row.task_id.slice(0, 8)}…
                      </td>
                      <td>{row.exchange}</td>
                      <td className="mono small">{row.inst_id}</td>
                      <td>
                        {row.position_side} · {row.action}
                      </td>
                      <td className="num">{fmtHeadNum(row.notional_usd)}</td>
                      <td className="num">{fmtOptionalHeadNum(row.filled_price)}</td>
                      <td>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function fmtNum(n: number, digits = 4): string {
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  const d = a >= 1000 ? 2 : a >= 1 ? digits : 6;
  return n.toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: 0 });
}

/** 头部指标统一 4 位小数 */
function fmtHeadNum(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(4);
}

function fmtFixed(n: number | null | undefined, digits: number): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

/** 账户纵览统一 2 位小数 */
function fmtOverviewNum(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function fmtOverviewSignedPnl(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${fmtOverviewNum(n)}`;
}

function fmtOverviewPnlRatio(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

function fmtOptionalHeadNum(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return fmtHeadNum(n);
}

/** 资金费率比例 → 百分比，4 位小数 */
function fmtFundingRatio(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(4)}%`;
}

function shortExchangeLabel(exchange: string): string {
  const map: Record<string, string> = {
    OKX: 'OKX',
    Bybit: 'BB',
    Binance: 'BN',
    Gateio: 'GT',
    Bitget: 'BG',
    Hyperliquid: 'HL',
    Lighter: 'LT',
    Aster: 'AS',
    EdgeX: 'EX',
    Paradex: 'PD',
  };
  return map[exchange] ?? exchange.slice(0, 4);
}

function groupTotalUsdValue(g: PositionGroupView): number {
  return (g.long_position?.usd_value ?? 0) + (g.short_position?.usd_value ?? 0);
}

/** 开仓时两腿名义合计（当前名义 − 浮动盈亏） */
function groupInitialInvestedUsd(g: PositionGroupView): number {
  let total = 0;
  for (const p of positionsInGroup(g)) {
    const entry = p.usd_value - (p.unrealized_pnl ?? 0);
    if (Number.isFinite(entry) && entry > 0) {
      total += entry;
    }
  }
  return total;
}

function groupUnrealizedPnl(g: PositionGroupView): number {
  let total = 0;
  for (const p of positionsInGroup(g)) {
    total += p.unrealized_pnl ?? 0;
  }
  return total;
}

/** 分组开仓起点：优先 last_open_position_time，否则取两腿 created_time 较早者 */
function groupOpenStartedMs(g: PositionGroupView): number | null {
  if (g.last_open_position_time != null && g.last_open_position_time > 0) {
    return g.last_open_position_time;
  }
  const times = positionsInGroup(g)
    .map((p) => p.created_time)
    .filter((t) => t > 0);
  if (times.length === 0) return null;
  return Math.min(...times);
}

function sortGroupsByUsdDesc(list: PositionGroupView[]): PositionGroupView[] {
  return [...list].sort((a, b) => groupTotalUsdValue(b) - groupTotalUsdValue(a));
}

function sortAccountsByUsdDesc(list: AccountInfoView[]): AccountInfoView[] {
  return [...list].sort((a, b) => b.total_usd - a.total_usd);
}

function portfolioPositionTotals(groups: PositionGroupView[]): {
  notionalUsd: number;
  unrealizedPnl: number;
} {
  let notionalUsd = 0;
  let unrealizedPnl = 0;
  for (const g of groups) {
    notionalUsd += groupTotalUsdValue(g);
    for (const p of positionsInGroup(g)) {
      unrealizedPnl += p.unrealized_pnl ?? 0;
    }
  }
  return { notionalUsd, unrealizedPnl };
}

function totalAccountEquity(accounts: AccountInfoView[]): number {
  return accounts.reduce(
    (sum, account) => sum + (Number.isFinite(account.total_usd) ? account.total_usd : 0),
    0
  );
}

/** 单腿仓位名义价值（Long/Short 均取绝对值后累加）。 */
function positionAbsUsdValue(p: PositionInGroupView): number {
  const v = p.usd_value ?? 0;
  return Number.isFinite(v) ? Math.abs(v) : 0;
}

/** 按交易所汇总 REST 同步仓位名义价值（不区分多空方向）。 */
function buildExchangePositionNotionalMap(
  syncedPositions: SyncedPositionsView[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (const bucket of syncedPositions) {
    const total = bucket.positions.reduce((sum, p) => sum + positionAbsUsdValue(p), 0);
    map.set(bucket.exchange, total);
  }
  return map;
}

function fmtPositionShareRatio(positionUsd: number, accountUsd: number): string {
  if (!Number.isFinite(positionUsd) || !Number.isFinite(accountUsd) || accountUsd <= 0) {
    return '—';
  }
  return `${((positionUsd / accountUsd) * 100).toFixed(2)}%`;
}

function fmtSignedPnl(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${fmtHeadNum(n)}`;
}

function pnlToneClass(n: number): string {
  if (!Number.isFinite(n) || Math.abs(n) < 1e-8) return '';
  return n > 0 ? 'is-profit' : 'is-loss';
}

function fmtImMmRatio(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
}

function fmtPnlRatio(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(4)}%`;
}

function fmtPnlRatioCompact(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

/** 根据 mark / avg 与方向计算持仓涨跌幅（与交易所 upl_ratio 一致） */
function positionPriceChangeRatio(p: PositionInGroupView): number | null {
  const avg = p.average_price;
  const mark = p.mark_price;
  if (!Number.isFinite(avg) || !Number.isFinite(mark) || avg <= 0) return null;
  const side = p.side.trim().toLowerCase();
  if (side === 'short' || side === 'sell') {
    return (avg - mark) / avg;
  }
  return (mark - avg) / avg;
}

/** 当前标记价距强平价的比例 |mark - liq| / mark */
function liqDistanceRatio(p: PositionInGroupView): number | null {
  const mark = p.mark_price;
  const liq = p.liq_price;
  if (!Number.isFinite(mark) || !Number.isFinite(liq) || mark <= 0 || liq <= 0) return null;
  return Math.abs(mark - liq) / mark;
}

function fmtLiqDistanceRatio(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(2)}%`;
}

function formatFundingCountdown(nextMs: number | null, nowMs = Date.now()): string {
  if (nextMs == null || !Number.isFinite(nextMs)) return '—';
  const diff = nextMs - nowMs;
  if (diff <= 0) return '已结算';
  const totalMin = Math.floor(diff / 60_000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return `${hours}h ${minutes}m`;
}

function PositionGroupDirection({ g }: { g: PositionGroupView }) {
  const longEx = g.long_position?.exchange;
  const shortEx = g.short_position?.exchange;
  if (!longEx || !shortEx) {
    return <span className="realtime-spread-pos-group-metric-value">—</span>;
  }
  return (
    <span className="realtime-spread-pos-group-direction">
      <span className="realtime-spread-side-chip is-long" title={`Long ${longEx}`}>
        L&nbsp;{shortExchangeLabel(longEx)}
      </span>
      <span className="realtime-spread-side-chip is-short" title={`Short ${shortEx}`}>
        S&nbsp;{shortExchangeLabel(shortEx)}
      </span>
    </span>
  );
}

function PositionGroupHeadMetrics({ g }: { g: PositionGroupView }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const openStartedMs = groupOpenStartedMs(g);
  const unrealizedPnl = groupUnrealizedPnl(g);
  const currentNotionalUsd = groupTotalUsdValue(g);
  const initialInvestedUsd = groupInitialInvestedUsd(g);
  const unrealizedPnlRatio =
    initialInvestedUsd > 0 && Number.isFinite(unrealizedPnl)
      ? unrealizedPnl / initialInvestedUsd
      : null;

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="realtime-spread-pos-group-metrics">
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">初始金额</span>
        <span
          className="realtime-spread-pos-group-metric-value"
          title="两腿开仓名义合计（当前名义 − 浮动盈亏）"
        >
          {fmtHeadNum(initialInvestedUsd)}
        </span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">仓位金额</span>
        <span className="realtime-spread-pos-group-metric-value" title="当前两腿仓位名义合计">
          {fmtOverviewNum(currentNotionalUsd)}
        </span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">持仓时长</span>
        <span className="realtime-spread-pos-group-metric-value">
          {openStartedMs != null ? fmtDurationHm(openStartedMs, nowMs) : '—'}
        </span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">浮动盈亏</span>
        <span
          className={`realtime-spread-pos-group-metric-value ${pnlToneClass(unrealizedPnl)}`}
          title={
            unrealizedPnlRatio != null
              ? `相对初始投入 ${fmtPnlRatio(unrealizedPnlRatio)}`
              : 'Long + Short 未实现盈亏合计'
          }
        >
          {fmtSignedPnl(unrealizedPnl)}
          {unrealizedPnlRatio != null ? (
            <span className="realtime-spread-pos-group-metric-sub">
              {' '}
              ({fmtPnlRatio(unrealizedPnlRatio)})
            </span>
          ) : null}
        </span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">均开仓价差</span>
        <span className="realtime-spread-pos-group-metric-value">{fmtOptionalHeadNum(g.avg_open_spread)}</span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">当前价差</span>
        <span className="realtime-spread-pos-group-metric-value">
          {fmtOptionalHeadNum(g.current_orderbook_spread)}
        </span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">净资金费</span>
        <span className="realtime-spread-pos-group-metric-value">{fmtHeadNum(g.funding_fee_net)}</span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">下次资金费</span>
        <span className="realtime-spread-pos-group-metric-value">
          {fmtOptionalHeadNum(g.estimated_next_funding_fee_net)}
        </span>
      </div>
      <div className="realtime-spread-pos-group-metric">
        <span className="realtime-spread-pos-group-metric-label">距结算</span>
        <span className="realtime-spread-pos-group-metric-value">
          {formatFundingCountdown(g.next_funding_time_ms)}
        </span>
      </div>
    </div>
  );
}

function fmtTs(ms: number): string {
  if (!ms && ms !== 0) return '—';
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19);
}

/** 创建时间距当前的时长（总小时 + 分钟） */
function fmtDurationHm(createdMs: number, nowMs: number): string {
  if (!createdMs || !Number.isFinite(createdMs)) return '—';
  const delta = Math.max(0, nowMs - createdMs);
  const totalMinutes = Math.floor(delta / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

/** UI 关仓模式 → trading-engine `ThresholdMode` */
function closeThresholdMode(mode: 'Immediate' | 'SpreadThreshold'): OpenInstructionBody['threshold_mode'] {
  return mode === 'SpreadThreshold' ? 'LessOrEqual' : 'Immediate';
}

type GroupInstructionMode = 'open' | 'close';

type GroupOpenFormProps = {
  totalNotional: string;
  setTotalNotional: (v: string) => void;
  perOrderNotional: string;
  setPerOrderNotional: (v: string) => void;
  thresholdMode: OpenInstructionBody['threshold_mode'];
  setThresholdMode: (v: OpenInstructionBody['threshold_mode']) => void;
  spreadThreshold: string;
  setSpreadThreshold: (v: string) => void;
  minIntervalMs: string;
  setMinIntervalMs: (v: string) => void;
};

type GroupCloseFormProps = {
  validity: InstructionValidityView;
  setValidity: (v: InstructionValidityView) => void;
  sizeMode: 'token' | 'usd';
  setSizeMode: (v: 'token' | 'usd') => void;
  targetAmount: string;
  setTargetAmount: (v: string) => void;
  perOrderNotional: string;
  setPerOrderNotional: (v: string) => void;
  closeMode: 'Immediate' | 'SpreadThreshold';
  setCloseMode: (v: 'Immediate' | 'SpreadThreshold') => void;
  spreadThreshold: string;
  setSpreadThreshold: (v: string) => void;
  minIntervalMs: string;
  setMinIntervalMs: (v: string) => void;
};

function PositionGroupOpenForm({
  g,
  busy,
  anyBusy,
  onSubmit,
  totalNotional,
  setTotalNotional,
  perOrderNotional,
  setPerOrderNotional,
  thresholdMode,
  setThresholdMode,
  spreadThreshold,
  setSpreadThreshold,
  minIntervalMs,
  setMinIntervalMs,
}: GroupOpenFormProps & {
  g: PositionGroupView;
  busy: boolean;
  anyBusy: boolean;
  onSubmit: () => void;
}) {
  const hedge = hedgeParamsFromGroup(g);
  const hedgeOk = !('error' in hedge);
  const incompleteGroup = !g.long_position || !g.short_position;
  const hedgeError = 'error' in hedge ? hedge.error : undefined;
  const hp = hedgeOk ? hedge : null;

  return (
    <div className="realtime-spread-pos-instruction-form">
      <p className="realtime-spread-pos-action-hint">
        开仓指令固定为单次有效：累计执行达到目标名义后结束。
      </p>
      {hp ? (
        <p className="realtime-spread-pos-action-hint mono small">
          Long {hp.long_exchange} · {hp.long_inst_id}　/　Short {hp.short_exchange} · {hp.short_inst_id}
        </p>
      ) : null}
      <label className="realtime-spread-pos-instruction-label">
        触发模式
        <select
          value={thresholdMode}
          disabled={anyBusy}
          onChange={(e) =>
            setThresholdMode(e.target.value as OpenInstructionBody['threshold_mode'])
          }
        >
          <option value="GreaterOrEqual">价差 ≥ 阈值（GreaterOrEqual）</option>
          <option value="Immediate">立即触发（Immediate）</option>
        </select>
      </label>
      {thresholdMode !== 'Immediate' ? (
        <label className="realtime-spread-pos-instruction-label">
          开仓价差阈值 threshold（相对 long ask）
          <input
            value={spreadThreshold}
            disabled={anyBusy}
            onChange={(e) => setSpreadThreshold(e.target.value)}
          />
        </label>
      ) : null}
      <label className="realtime-spread-pos-instruction-label">
        目标名义 target_notional (USD)
        <input
          type="number"
          min={0}
          step="any"
          value={totalNotional}
          disabled={anyBusy}
          onChange={(e) => setTotalNotional(e.target.value)}
        />
      </label>
      <label className="realtime-spread-pos-instruction-label">
        单笔名义 per_order_notional (USD)
        <input
          type="number"
          min={0}
          step="any"
          value={perOrderNotional}
          disabled={anyBusy}
          onChange={(e) => setPerOrderNotional(e.target.value)}
        />
      </label>
      <label className="realtime-spread-pos-instruction-label">
        最短下单间隔 min_order_interval_ms
        <input
          type="number"
          min={0}
          step={1}
          value={minIntervalMs}
          disabled={anyBusy}
          onChange={(e) => setMinIntervalMs(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="realtime-spread-positions-btn primary realtime-spread-pos-instruction-submit"
        disabled={anyBusy || !hedgeOk || incompleteGroup}
        title={hedgeError}
        onClick={onSubmit}
      >
        {busy ? '提交中…' : '提交开仓指令'}
      </button>
      {hedgeError ? <p className="realtime-spread-pos-action-hint error">{hedgeError}</p> : null}
      {incompleteGroup ? (
        <p className="realtime-spread-pos-action-hint">该分组需同时有 Long 与 Short 仓位才可开仓。</p>
      ) : null}
    </div>
  );
}

function PositionGroupCloseForm({
  g,
  busy,
  anyBusy,
  onSubmit,
  validity,
  setValidity,
  sizeMode,
  setSizeMode,
  targetAmount,
  setTargetAmount,
  perOrderNotional,
  setPerOrderNotional,
  closeMode,
  setCloseMode,
  spreadThreshold,
  setSpreadThreshold,
  minIntervalMs,
  setMinIntervalMs,
}: GroupCloseFormProps & {
  g: PositionGroupView;
  busy: boolean;
  anyBusy: boolean;
  onSubmit: () => void;
}) {
  const hedge = hedgeParamsFromGroup(g);
  const hedgeOk = !('error' in hedge);
  const hedgeError = 'error' in hedge ? hedge.error : undefined;
  const hp = hedgeOk ? hedge : null;

  return (
    <div className="realtime-spread-pos-instruction-form">
      {hp ? (
        <p className="realtime-spread-pos-action-hint mono small">
          Long {hp.long_exchange} · {hp.long_inst_id}　/　Short {hp.short_exchange} · {hp.short_inst_id}
        </p>
      ) : null}
      <p className="realtime-spread-pos-action-hint">
        单次关仓需存在对应方向的开仓指令或对冲仓位，可指定总关仓 token 或 USD 名义；永久关仓持续监听价差直至手动取消。
      </p>
      <label className="realtime-spread-pos-instruction-label">
        指令有效期 validity
        <select
          value={validity}
          disabled={anyBusy}
          onChange={(e) => setValidity(e.target.value as InstructionValidityView)}
        >
          <option value="one_shot">单次有效（one_shot）</option>
          <option value="persistent">永久有效（persistent）</option>
        </select>
      </label>
      <label className="realtime-spread-pos-instruction-label">
        触发模式
        <select
          value={closeMode}
          disabled={anyBusy}
          onChange={(e) =>
            setCloseMode(e.target.value as 'Immediate' | 'SpreadThreshold')
          }
        >
          <option value="Immediate">立即触发（Immediate）</option>
          <option value="SpreadThreshold">价差 ≤ 阈值（LessOrEqual）</option>
        </select>
      </label>
      {closeMode === 'SpreadThreshold' ? (
        <label className="realtime-spread-pos-instruction-label">
          关仓价差阈值 threshold（相对 short ask）
          <input
            value={spreadThreshold}
            disabled={anyBusy}
            onChange={(e) => setSpreadThreshold(e.target.value)}
          />
        </label>
      ) : null}
      {validity === 'one_shot' ? (
        <>
          <label className="realtime-spread-pos-instruction-label">
            总关仓数量类型
            <select
              value={sizeMode}
              disabled={anyBusy}
              onChange={(e) => setSizeMode(e.target.value as 'token' | 'usd')}
            >
              <option value="usd">USD 名义</option>
              <option value="token">Token 数量</option>
            </select>
          </label>
          <label className="realtime-spread-pos-instruction-label">
            {sizeMode === 'usd' ? '总关仓 USD 名义' : '总关仓 token 数量'}
            <input
              type="number"
              min={0}
              step="any"
              value={targetAmount}
              disabled={anyBusy}
              onChange={(e) => setTargetAmount(e.target.value)}
            />
          </label>
        </>
      ) : null}
      <label className="realtime-spread-pos-instruction-label">
        单次关仓名义 (USD)
        <input
          type="number"
          min={0}
          step="any"
          value={perOrderNotional}
          disabled={anyBusy}
          onChange={(e) => setPerOrderNotional(e.target.value)}
        />
      </label>
      <label className="realtime-spread-pos-instruction-label">
        最短下单间隔 min_order_interval_ms
        <input
          type="number"
          min={0}
          step={1}
          value={minIntervalMs}
          disabled={anyBusy}
          onChange={(e) => setMinIntervalMs(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="realtime-spread-positions-btn realtime-spread-pos-instruction-submit"
        disabled={anyBusy || !hedgeOk}
        title={hedgeError}
        onClick={onSubmit}
      >
        {busy ? '提交中…' : '提交关仓指令'}
      </button>
      {hedgeError ? <p className="realtime-spread-pos-action-hint error">{hedgeError}</p> : null}
    </div>
  );
}

function useDialogDrag() {
  const offsetRef = useRef({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragSession = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const applyOffset = (next: { x: number; y: number }) => {
    offsetRef.current = next;
    setOffset(next);
  };

  const onHeaderPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button')) return;
    dragSession.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: offsetRef.current.x,
      originY: offsetRef.current.y,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onHeaderPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragSession.current) return;
    applyOffset({
      x: dragSession.current.originX + event.clientX - dragSession.current.startX,
      y: dragSession.current.originY + event.clientY - dragSession.current.startY,
    });
  };

  const onHeaderPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    if (!dragSession.current) return;
    dragSession.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return {
    offset,
    dragging,
    headerDragHandlers: {
      onPointerDown: onHeaderPointerDown,
      onPointerMove: onHeaderPointerMove,
      onPointerUp: onHeaderPointerUp,
      onPointerCancel: onHeaderPointerUp,
    },
  };
}

type PositionGroupInstructionDialogProps = {
  g: PositionGroupView;
  mode: GroupInstructionMode;
  onClose: () => void;
  dialogError: string | null;
  busy: boolean;
  anyBusy: boolean;
  onSubmitOpen: () => void;
  onSubmitClose: () => void;
  openForm: GroupOpenFormProps;
  closeForm: GroupCloseFormProps;
};

function PositionGroupInstructionDialog({
  g,
  mode,
  onClose,
  dialogError,
  busy,
  anyBusy,
  onSubmitOpen,
  onSubmitClose,
  openForm,
  closeForm,
}: PositionGroupInstructionDialogProps) {
  const { offset, dragging, headerDragHandlers } = useDialogDrag();

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const symbol = groupSymbol(g) || g.group_id;
  const isOpen = mode === 'open';

  return (
    <div
      className="realtime-spread-pos-modal-overlay realtime-spread-pos-action-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="realtime-spread-pos-modal realtime-spread-pos-action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="position-group-instruction-title"
        onClick={(ev) => ev.stopPropagation()}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      >
        <header
          className={`realtime-spread-pos-modal-head realtime-spread-pos-action-modal-head${dragging ? ' is-dragging' : ''}`}
          {...headerDragHandlers}
        >
          <div>
            <h2 id="position-group-instruction-title">
              {isOpen ? '开仓' : '关仓'} · {symbol}
            </h2>
            <p className="realtime-spread-pos-modal-sub">
              参数与手动交易面板一致，基于当前分组 Long/Short 腿自动填充交易所与 inst_id。
            </p>
          </div>
          <button
            type="button"
            className="realtime-spread-pos-modal-close"
            aria-label="关闭"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="realtime-spread-pos-modal-body">
          {dialogError ? (
            <div className="realtime-spread-positions-error realtime-spread-pos-instruction-error">
              {dialogError}
            </div>
          ) : null}
          {isOpen ? (
            <PositionGroupOpenForm
              g={g}
              busy={busy}
              anyBusy={anyBusy}
              onSubmit={onSubmitOpen}
              totalNotional={openForm.totalNotional}
              setTotalNotional={openForm.setTotalNotional}
              perOrderNotional={openForm.perOrderNotional}
              setPerOrderNotional={openForm.setPerOrderNotional}
              thresholdMode={openForm.thresholdMode}
              setThresholdMode={openForm.setThresholdMode}
              spreadThreshold={openForm.spreadThreshold}
              setSpreadThreshold={openForm.setSpreadThreshold}
              minIntervalMs={openForm.minIntervalMs}
              setMinIntervalMs={openForm.setMinIntervalMs}
            />
          ) : (
            <PositionGroupCloseForm
              g={g}
              busy={busy}
              anyBusy={anyBusy}
              onSubmit={onSubmitClose}
              validity={closeForm.validity}
              setValidity={closeForm.setValidity}
              sizeMode={closeForm.sizeMode}
              setSizeMode={closeForm.setSizeMode}
              targetAmount={closeForm.targetAmount}
              setTargetAmount={closeForm.setTargetAmount}
              perOrderNotional={closeForm.perOrderNotional}
              setPerOrderNotional={closeForm.setPerOrderNotional}
              closeMode={closeForm.closeMode}
              setCloseMode={closeForm.setCloseMode}
              spreadThreshold={closeForm.spreadThreshold}
              setSpreadThreshold={closeForm.setSpreadThreshold}
              minIntervalMs={closeForm.minIntervalMs}
              setMinIntervalMs={closeForm.setMinIntervalMs}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Long / Short 着色：兼容后端枚举名与 buy/sell */
function sideChipClass(sideRaw: string): string {
  const s = sideRaw.trim().toLowerCase();
  if (s === 'long' || s === 'buy') return 'realtime-spread-side-chip is-long';
  if (s === 'short' || s === 'sell') return 'realtime-spread-side-chip is-short';
  return 'realtime-spread-side-chip is-neutral';
}

function fmtOptionalNum(n: number | null, digits = 4): string {
  if (n == null) return '—';
  return fmtNum(n, digits);
}

/** 分组内 Long/Short 仓位列表（固定顺序：Long → Short） */
function positionsInGroup(g: PositionGroupView): PositionInGroupView[] {
  const out: PositionInGroupView[] = [];
  if (g.long_position) out.push(g.long_position);
  if (g.short_position) out.push(g.short_position);
  return out;
}

function groupSymbol(g: PositionGroupView): string {
  return (g.long_position?.symbol ?? g.short_position?.symbol ?? '').trim();
}

/** 从分组内 Long/Short 仓位推导对冲下单参数。 */
function hedgeParamsFromGroup(
  g: PositionGroupView
):
  | {
      symbol: string;
      long_exchange: string;
      short_exchange: string;
      long_inst_id: string;
      short_inst_id: string;
    }
  | { error: string } {
  const longPos = g.long_position;
  const shortPos = g.short_position;
  if (!longPos || !shortPos) {
    return { error: '该分组需要 Long 与 Short 仓位才能加仓/减仓。' };
  }
  if (longPos.exchange === shortPos.exchange) {
    return { error: '多空在同一交易所，无法按对冲接口加仓/减仓。' };
  }
  const long_inst_id = longPos.inst_id.trim();
  const short_inst_id = shortPos.inst_id.trim();
  if (!long_inst_id || !short_inst_id) {
    return { error: '缺少合约 id（inst_id 为空）。' };
  }
  const symbol = groupSymbol(g);
  if (!symbol) {
    return { error: '缺少 symbol。' };
  }
  return {
    symbol: normalizeSymbolInput(symbol),
    long_exchange: longPos.exchange,
    short_exchange: shortPos.exchange,
    long_inst_id,
    short_inst_id,
  };
}

function AccountDetailPanel({
  account,
  positionNotionalUsd,
}: {
  account: AccountInfoView;
  positionNotionalUsd: number;
}) {
  return (
    <div className="realtime-spread-account-detail">
      <div className="realtime-spread-account-detail-head">
        <strong>{account.exchange}</strong>
        <span className="realtime-spread-account-equity num">{fmtOverviewNum(account.total_usd)} USD</span>
        <span className="realtime-spread-account-position num">
          仓位 {fmtOverviewNum(positionNotionalUsd)} (
          {fmtPositionShareRatio(positionNotionalUsd, account.total_usd)})
        </span>
        <span className="realtime-spread-account-rates">
          IM {fmtImMmRatio(account.im_rate)} · MM {fmtImMmRatio(account.mm_rate)}
        </span>
      </div>
      {account.assets.length > 0 ? (
        <table className="realtime-spread-account-assets">
          <thead>
            <tr>
              <th>资产</th>
              <th className="num">余额</th>
              <th className="num">权益</th>
              <th className="num">USD</th>
            </tr>
          </thead>
          <tbody>
            {account.assets.map((asset) => (
              <tr key={`${account.exchange}-${asset.asset}`}>
                <td>{asset.asset}</td>
                <td className="num">{fmtNum(asset.balance)}</td>
                <td className="num">{fmtNum(asset.equity)}</td>
                <td className="num">{fmtOverviewNum(asset.usd_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="realtime-spread-account-empty">暂无资产明细</p>
      )}
    </div>
  );
}

function PortfolioHeaderSummary({
  groups,
  syncedPositions,
  accounts,
  dailyPnl,
  variant = 'inline',
}: {
  groups: PositionGroupView[];
  syncedPositions: SyncedPositionsView[];
  accounts: AccountInfoView[];
  dailyPnl: DailyAccountPnlView | null;
  variant?: 'inline' | 'sidebar';
}) {
  const [expandedExchange, setExpandedExchange] = useState<string | null>(null);
  const totals = portfolioPositionTotals(groups);
  const equity = totalAccountEquity(accounts);
  const sortedAccounts = sortAccountsByUsdDesc(accounts);
  const positionNotionalByExchange = useMemo(
    () => buildExchangePositionNotionalMap(syncedPositions),
    [syncedPositions],
  );
  const dailyAccountPnl = dailyPnl?.daily_pnl ?? null;
  const hasDailyBaseline = dailyPnl?.baseline_total_usd != null;
  const expandedAccount = sortedAccounts.find((account) => account.exchange === expandedExchange) ?? null;
  const expandedPositionNotional = expandedAccount
    ? (positionNotionalByExchange.get(expandedAccount.exchange) ?? 0)
    : 0;

  const overviewClassName =
    variant === 'sidebar'
      ? 'realtime-spread-portfolio-overview is-sidebar'
      : 'realtime-spread-portfolio-overview is-compact';
  const kpisClassName =
    variant === 'sidebar'
      ? 'realtime-spread-portfolio-kpis is-sidebar'
      : 'realtime-spread-portfolio-kpis is-compact';

  return (
    <div className={overviewClassName}>
      {variant === 'sidebar' ? <h3 className="realtime-spread-portfolio-sidebar-title">账户信息</h3> : null}
      <div className={kpisClassName}>
        <div className="realtime-spread-portfolio-kpi is-compact">
          <span className="realtime-spread-portfolio-kpi-label">仓位总价值</span>
          <span className="realtime-spread-portfolio-kpi-value">{fmtOverviewNum(totals.notionalUsd)}</span>
        </div>
        <div className="realtime-spread-portfolio-kpi is-compact">
          <span className="realtime-spread-portfolio-kpi-label">持仓浮动盈亏</span>
          <span className={`realtime-spread-portfolio-kpi-value ${pnlToneClass(totals.unrealizedPnl)}`}>
            {fmtOverviewSignedPnl(totals.unrealizedPnl)}
          </span>
        </div>
        <div className="realtime-spread-portfolio-kpi is-compact">
          <span
            className="realtime-spread-portfolio-kpi-label"
            title={
              hasDailyBaseline
                ? `基准 ${fmtOverviewNum(dailyPnl!.baseline_total_usd!)} → 当前 ${fmtOverviewNum(dailyPnl!.current_total_usd)}`
                : '当日首次账户同步后将建立基准'
            }
          >
            当日盈利
          </span>
          <span className={`realtime-spread-portfolio-kpi-value ${pnlToneClass(dailyAccountPnl ?? 0)}`}>
            {hasDailyBaseline ? fmtOverviewSignedPnl(dailyAccountPnl!) : '—'}
          </span>
        </div>
        <div className="realtime-spread-portfolio-kpi is-compact">
          <span className="realtime-spread-portfolio-kpi-label">当日收益率</span>
          <span className={`realtime-spread-portfolio-kpi-value ${pnlToneClass(dailyPnl?.daily_pnl_ratio ?? 0)}`}>
            {hasDailyBaseline ? fmtOverviewPnlRatio(dailyPnl!.daily_pnl_ratio) : '—'}
          </span>
        </div>
        <div className="realtime-spread-portfolio-kpi is-compact">
          <span className="realtime-spread-portfolio-kpi-label">账户权益</span>
          <span className="realtime-spread-portfolio-kpi-value">
            {accounts.length > 0 ? fmtOverviewNum(equity) : '—'}
          </span>
        </div>
        <div className="realtime-spread-portfolio-kpi is-compact">
          <span className="realtime-spread-portfolio-kpi-label">持仓组数</span>
          <span className="realtime-spread-portfolio-kpi-value">{groups.length}</span>
        </div>
      </div>
      {sortedAccounts.length > 0 ? (
        <div className="realtime-spread-accounts-compact">
          {sortedAccounts.map((account) => {
            const isOpen = expandedExchange === account.exchange;
            const positionNotionalUsd = positionNotionalByExchange.get(account.exchange) ?? 0;
            const positionShareRatio = fmtPositionShareRatio(positionNotionalUsd, account.total_usd);
            return (
              <button
                key={account.exchange}
                type="button"
                className={`realtime-spread-account-chip${isOpen ? ' is-open' : ''}`}
                aria-expanded={isOpen}
                title={`${account.exchange} 权益 ${fmtOverviewNum(account.total_usd)} · 仓位 ${fmtOverviewNum(positionNotionalUsd)} · 占比 ${positionShareRatio}`}
                onClick={() =>
                  setExpandedExchange((prev) => (prev === account.exchange ? null : account.exchange))
                }
              >
                <span className="realtime-spread-account-chip-name">{account.exchange}</span>
                <span className="realtime-spread-account-chip-metrics">
                  <span className="realtime-spread-account-chip-metric">
                    <span className="realtime-spread-account-chip-metric-label">权益</span>
                    <span className="realtime-spread-account-chip-usd num">{fmtOverviewNum(account.total_usd)}</span>
                  </span>
                  <span className="realtime-spread-account-chip-divider" aria-hidden="true">
                    ·
                  </span>
                  <span className="realtime-spread-account-chip-metric">
                    <span className="realtime-spread-account-chip-metric-label">仓位</span>
                    <span className="realtime-spread-account-chip-position num">
                      {fmtOverviewNum(positionNotionalUsd)}
                    </span>
                    <span className="realtime-spread-account-chip-ratio">({positionShareRatio})</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="realtime-spread-portfolio-hint is-compact">暂无账户缓存，等待 WS 推送账户快照。</p>
      )}
      {expandedAccount ? (
        <AccountDetailPanel
          account={expandedAccount}
          positionNotionalUsd={expandedPositionNotional}
        />
      ) : null}
    </div>
  );
}

function positionLegRowKey(
  exchange: string,
  position: PositionInGroupView,
  scope?: string,
): string {
  const base = `${exchange}-${position.inst_id}-${position.side}`;
  return scope ? `${scope}-${base}` : base;
}

function PositionGroupDetailTable({
  g,
  onPositionAction,
  actionBusyKey,
}: {
  g: PositionGroupView;
  onPositionAction?: (position: PositionInGroupView, mode: PositionLegActionMode) => void;
  actionBusyKey?: string | null;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const positions = positionsInGroup(g);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <>
      {positions.length === 0 ? (
        <p className="realtime-spread-positions-empty">该分组暂无 Long/Short 仓位。</p>
      ) : (
        <div className="realtime-spread-leg-summary-list">
          {positions.map((p, li) => {
            const priceChange = positionPriceChangeRatio(p);
            const liqDist = liqDistanceRatio(p);
            const rowKey = positionLegRowKey(p.exchange, p, g.group_id);
            const busy = actionBusyKey === rowKey;
            return (
              <div
                key={`${g.group_id}-${p.exchange}-${p.inst_id}-${p.side}-${li}`}
                className="realtime-spread-leg-summary-row"
              >
                <div className="realtime-spread-leg-summary-id">
                  <strong>{p.exchange}</strong>
                  <span className={sideChipClass(p.side)}>{p.side}</span>
                  <code title={p.inst_id}>{p.inst_id}</code>
                </div>
                <div className="realtime-spread-leg-summary-metrics">
                  <span>
                    <em>数量</em>
                    <strong>{fmtNum(p.size)}</strong>
                  </span>
                  <span>
                    <em>名义</em>
                    <strong>{fmtNum(p.usd_value, 2)}</strong>
                  </span>
                  <span>
                    <em>均价/标记</em>
                    <strong>{fmtFixed(p.average_price, 4)} / {fmtFixed(p.mark_price, 4)}</strong>
                  </span>
                  <span>
                    <em>涨跌</em>
                    <strong className={pnlToneClass(priceChange ?? 0)}>{fmtPnlRatioCompact(priceChange)}</strong>
                  </span>
                  <span>
                    <em>距强平</em>
                    <strong className={liqDist != null && liqDist < 0.3 ? 'is-loss' : ''}>
                      {fmtLiqDistanceRatio(liqDist)}
                    </strong>
                  </span>
                  <span>
                    <em>未实现</em>
                    <strong className={pnlToneClass(p.unrealized_pnl)}>{fmtNum(p.unrealized_pnl, 2)}</strong>
                  </span>
                  <span>
                    <em>下次资金费</em>
                    <strong title={p.next_funding_time_ms != null ? fmtTs(p.next_funding_time_ms) : undefined}>
                      {fmtFixed(p.next_funding_fee, 2)}
                    </strong>
                  </span>
                  <span>
                    <em>距结算</em>
                    <strong>{formatFundingCountdown(p.next_funding_time_ms, nowMs)}</strong>
                  </span>
                </div>
                {onPositionAction ? (
                  <div className="realtime-spread-leg-summary-actions">
                    <button
                      type="button"
                      className="realtime-spread-positions-btn"
                      disabled={busy || actionBusyKey != null}
                      onClick={() => onPositionAction(p, 'add')}
                    >
                      开仓
                    </button>
                    <button
                      type="button"
                      className="realtime-spread-positions-btn"
                      disabled={busy || actionBusyKey != null}
                      onClick={() => onPositionAction(p, 'reduce')}
                    >
                      减仓
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <div className="realtime-spread-pos-group-extras">
        <span>目标关仓价差 {fmtOptionalNum(g.target_close_spread, 4)}</span>
        <span>开仓 {g.last_open_position_time != null ? fmtTs(g.last_open_position_time) : '—'}</span>
        <span>平仓 {g.last_close_position_time != null ? fmtTs(g.last_close_position_time) : '—'}</span>
      </div>
    </>
  );
}

function fmtSignedUsd(n: number, digits = 4): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${fmtNum(n, digits)}`;
}

function sortFundingRecordsDesc(records: FundingRecordView[]): FundingRecordView[] {
  return [...records].sort((a, b) => b.ts - a.ts);
}

function FundingHistoryTable({
  title,
  position,
}: {
  title: string;
  position: PositionInGroupView | null;
}) {
  const records = useMemo(
    () => (position ? sortFundingRecordsDesc(position.funding_history) : []),
    [position]
  );

  if (!position) {
    return (
      <div className="realtime-spread-pos-funding-section">
        <h3 className="realtime-spread-pos-funding-section-title">{title}</h3>
        <p className="realtime-spread-positions-empty">该腿暂无仓位。</p>
      </div>
    );
  }

  return (
    <div className="realtime-spread-pos-funding-section">
      <h3 className="realtime-spread-pos-funding-section-title">
        {title}
        <span className="realtime-spread-pos-funding-section-meta">
          {position.exchange} · {position.inst_id} · {position.side} · 累计{' '}
          <span className={pnlToneClass(position.funding_fee)}>{fmtSignedUsd(position.funding_fee)}</span>
        </span>
      </h3>
      <div className="realtime-spread-positions-table-wrap">
        <table className="realtime-spread-positions-table">
          <thead>
            <tr>
              <th>结算时间 (UTC)</th>
              <th className="num">费率</th>
              <th className="num">仓位价值 (USD)</th>
              <th className="num">结算金额 (USD)</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan={4} className="realtime-spread-positions-empty">
                  暂无资金费结算记录。
                </td>
              </tr>
            ) : (
              records.map((r, i) => (
                <tr key={`${position.inst_id}-${r.ts}-${i}`}>
                  <td className="mono small">{fmtTs(r.ts)}</td>
                  <td className="num">{fmtFundingRatio(r.rate)}</td>
                  <td className="num">{fmtNum(r.notional_usd, 2)}</td>
                  <td className={`num ${pnlToneClass(r.fee_value)}`}>{fmtSignedUsd(r.fee_value)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type PositionGroupDetailModalProps = {
  g: PositionGroupView;
  activeInstructions?: InstructionView[];
  onSelectInstruction?: (row: InstructionView) => void;
  onPositionAction?: (position: PositionInGroupView, mode: PositionLegActionMode) => void;
  actionBusyKey?: string | null;
  onClose: () => void;
};

function PositionGroupDetailModal({
  g,
  activeInstructions = [],
  onSelectInstruction,
  onPositionAction,
  actionBusyKey,
  onClose,
}: PositionGroupDetailModalProps) {
  const symbol = groupSymbol(g) || g.group_id;

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="realtime-spread-pos-modal-overlay realtime-spread-pos-detail-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="realtime-spread-pos-modal realtime-spread-pos-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="position-group-detail-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="realtime-spread-pos-modal-head">
          <div>
            <h2 id="position-group-detail-title">{symbol} · 分组详情</h2>
            <p className="realtime-spread-pos-modal-sub">
              <code className="mono">{g.group_id}</code>
              <span className="tag">{g.arbitrage_type}</span>
              {' · '}
              净资金费{' '}
              <span className={pnlToneClass(g.funding_fee_net)}>{fmtSignedUsd(g.funding_fee_net)}</span>
              {' · '}
              下次预估{' '}
              {g.estimated_next_funding_fee_net != null
                ? fmtSignedUsd(g.estimated_next_funding_fee_net)
                : '—'}
              {' · '}
              距结算 {formatFundingCountdown(g.next_funding_time_ms)}
            </p>
          </div>
          <button
            type="button"
            className="realtime-spread-pos-modal-close"
            aria-label="关闭"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="realtime-spread-pos-modal-body">
          <div className="realtime-spread-pos-detail-summary">
            <PositionGroupHeadMetrics g={g} />
          </div>
          <PositionGroupActiveInstructions
            instructions={activeInstructions}
            onSelectInstruction={onSelectInstruction}
          />
          <h3 className="realtime-spread-pos-detail-section-title">仓位明细</h3>
          <div className="realtime-spread-positions-table-wrap">
            <PositionGroupDetailTable
              g={g}
              onPositionAction={onPositionAction}
              actionBusyKey={actionBusyKey}
            />
          </div>
          <h3 className="realtime-spread-pos-detail-section-title">资金费结算记录</h3>
          <div className="realtime-spread-pos-funding-grid">
            <FundingHistoryTable title="Long 腿" position={g.long_position} />
            <FundingHistoryTable title="Short 腿" position={g.short_position} />
          </div>
        </div>
      </div>
    </div>
  );
}

type PositionGroupCardProps = {
  g: PositionGroupView;
  expanded: boolean;
  selected?: boolean;
  activeInstructions?: InstructionView[];
  autoBalanceEnabled?: boolean;
  autoBalanceBusy?: boolean;
  onAutoBalanceChange?: (enabled: boolean) => void;
  onToggle: () => void;
  onLinkChart?: (g: PositionGroupView) => void;
  /** 内联列表截断 group_id；弹窗展示完整 id */
  truncateGroupId?: boolean;
  onShowDetail?: (g: PositionGroupView) => void;
  onOpenAction?: (g: PositionGroupView) => void;
  onCloseAction?: (g: PositionGroupView) => void;
  onSelectInstruction?: (row: InstructionView) => void;
  onPositionAction?: (position: PositionInGroupView, mode: PositionLegActionMode) => void;
  actionBusyKey?: string | null;
};

function PositionGroupCard({
  g,
  expanded,
  selected = false,
  activeInstructions = [],
  autoBalanceEnabled = true,
  autoBalanceBusy = false,
  onAutoBalanceChange,
  onToggle,
  onLinkChart,
  truncateGroupId = true,
  onShowDetail,
  onOpenAction,
  onCloseAction,
  onSelectInstruction,
  onPositionAction,
  actionBusyKey,
}: PositionGroupCardProps) {
  const hedge = hedgeParamsFromGroup(g);
  const hedgeOk = !('error' in hedge);
  const positions = positionsInGroup(g);
  const symbol = groupSymbol(g) || g.group_id;
  const incompleteGroup = !g.long_position || !g.short_position;
  const groupIdLabel = truncateGroupId ? `${g.group_id.slice(0, 24)}…` : g.group_id;
  const disabled = !hedgeOk || incompleteGroup;
  const disabledTitle = hedgeOk
    ? incompleteGroup
      ? '该分组需同时有 Long 与 Short 仓位'
      : undefined
    : 'error' in hedge
      ? hedge.error
      : undefined;

  return (
    <div className={`realtime-spread-pos-group${selected ? ' is-chart-selected' : ''}`}>
      <div className="realtime-spread-pos-group-head">
        <div
          className="realtime-spread-pos-group-summary"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={onToggle}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              onToggle();
            }
          }}
        >
          <span className="realtime-spread-pos-group-chevron" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
          <span className="realtime-spread-pos-group-title">
            <span className="realtime-spread-pos-group-identity">
              {onLinkChart ? (
                <button
                  type="button"
                  className="realtime-spread-pos-group-symbol-link"
                  title="切换主图到此分组"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onLinkChart(g);
                  }}
                >
                  {symbol}
                </button>
              ) : (
                <strong>{symbol}</strong>
              )}
              <PositionGroupDirection g={g} />
            </span>
            <PositionGroupHeadMetrics g={g} />
            <span className="realtime-spread-pos-group-meta">
              <code className="mono">{groupIdLabel}</code>
              <span className="tag">{g.arbitrage_type}</span>
              <span className="sub">{positions.length} 仓</span>
            </span>
          </span>
        </div>
        {onShowDetail || onOpenAction || onCloseAction || onAutoBalanceChange ? (
          <div className="realtime-spread-pos-group-head-actions">
            {onAutoBalanceChange ? (
              <label
                className="realtime-spread-pos-auto-balance-toggle"
                title="自动平衡：两腿 size 不等时 reduce-only 平仓"
                onClick={(ev) => ev.stopPropagation()}
              >
                <span className="realtime-spread-pos-auto-balance-label">Auto</span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={`${symbol} auto balance`}
                  checked={autoBalanceEnabled}
                  disabled={autoBalanceBusy}
                  onChange={(ev) => onAutoBalanceChange(ev.target.checked)}
                />
              </label>
            ) : null}
            {onShowDetail ? (
              <button
                type="button"
                className="realtime-spread-positions-btn"
                title="查看分组详情与资金费结算记录"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onShowDetail(g);
                }}
              >
                详情
              </button>
            ) : null}
            {onOpenAction ? (
              <button
                type="button"
                className="realtime-spread-positions-btn primary"
                disabled={disabled}
                title={disabledTitle ?? '创建开仓指令'}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onOpenAction(g);
                }}
              >
                开仓
              </button>
            ) : null}
            {onCloseAction ? (
              <button
                type="button"
                className="realtime-spread-positions-btn"
                disabled={disabled}
                title={disabledTitle ?? '创建关仓指令'}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onCloseAction(g);
                }}
              >
                关仓
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <PositionGroupActiveInstructions
        instructions={activeInstructions}
        onSelectInstruction={onSelectInstruction}
      />
      {expanded ? (
        <div className="realtime-spread-pos-group-detail">
          <div className="realtime-spread-positions-table-wrap">
            <PositionGroupDetailTable
              g={g}
              onPositionAction={onPositionAction}
              actionBusyKey={actionBusyKey}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ExpandIcon() {
  return (
    <svg
      className="realtime-spread-positions-expand-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M3 3h6v2H6.41L10 8.59 8.59 10 5 6.41V9H3V3zm18 0v6h-2V6.41L15.41 10 14 8.59 17.59 5H15V3h6zm0 18h-6v-2h2.59L14 15.41 15.41 14 19 17.59V15h2v6zM3 21v-6h2v2.59L8.59 14 10 15.41 6.41 19H9v2H3z"
      />
    </svg>
  );
}

type PositionGroupsModalProps = {
  groups: PositionGroupView[];
  instructionIndex: Map<string, InstructionView[]>;
  lastRefreshAt: number | null;
  autoRefreshEnabled: boolean;
  error: string | null;
  expandedIds: Set<string>;
  selectedGroupId?: string | null;
  onToggleGroup: (g: PositionGroupView) => void;
  onLinkChart?: (g: PositionGroupView) => void;
  onClose: () => void;
  onShowDetail: (g: PositionGroupView) => void;
  onOpenAction: (g: PositionGroupView) => void;
  onCloseAction: (g: PositionGroupView) => void;
  onSelectInstruction?: (row: InstructionView) => void;
  onGroupPositionAction?: (
    g: PositionGroupView,
    position: PositionInGroupView,
    mode: PositionLegActionMode,
  ) => void;
  actionBusyKey?: string | null;
  groupSettingsById?: Map<string, boolean>;
  autoBalanceBusyId?: string | null;
  onAutoBalanceChange?: (g: PositionGroupView, enabled: boolean) => void;
};

function PositionGroupsModal({
  groups,
  instructionIndex,
  lastRefreshAt,
  autoRefreshEnabled,
  error,
  expandedIds,
  selectedGroupId = null,
  onToggleGroup,
  onLinkChart,
  onClose,
  onShowDetail,
  onOpenAction,
  onCloseAction,
  onSelectInstruction,
  onGroupPositionAction,
  actionBusyKey,
  groupSettingsById,
  autoBalanceBusyId,
  onAutoBalanceChange,
}: PositionGroupsModalProps) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="realtime-spread-pos-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="realtime-spread-pos-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="position-groups-modal-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="realtime-spread-pos-modal-head">
          <div>
            <h2 id="position-groups-modal-title">Position Groups</h2>
            <p className="realtime-spread-pos-modal-sub">
              {autoRefreshEnabled ? 'WS 每秒推送' : 'WS 推送已暂停'}
              {lastRefreshAt != null ? ` · 最近更新 ${fmtTs(lastRefreshAt)}` : ''}
              {groups.length > 0 ? ` · ${groups.length} 组` : ''}
            </p>
          </div>
          <button
            type="button"
            className="realtime-spread-pos-modal-close"
            aria-label="关闭"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="realtime-spread-pos-modal-body">
          {error ? <div className="realtime-spread-positions-error">{error}</div> : null}
          {groups.length === 0 ? (
            <p className="realtime-spread-positions-empty">暂无 position group。</p>
          ) : (
            <div className="realtime-spread-pos-groups">
              {groups.map((g) => (
                <PositionGroupCard
                  key={g.group_id}
                  g={g}
                  expanded={expandedIds.has(g.group_id)}
                  selected={selectedGroupId === g.group_id}
                  activeInstructions={activeInstructionsForGroup(g, instructionIndex)}
                  autoBalanceEnabled={groupSettingsById?.get(g.group_id) ?? true}
                  autoBalanceBusy={autoBalanceBusyId === g.group_id}
                  onAutoBalanceChange={
                    onAutoBalanceChange
                      ? (enabled) => onAutoBalanceChange(g, enabled)
                      : undefined
                  }
                  onToggle={() => onToggleGroup(g)}
                  onLinkChart={onLinkChart}
                  truncateGroupId={false}
                  onShowDetail={onShowDetail}
                  onOpenAction={onOpenAction}
                  onCloseAction={onCloseAction}
                  onSelectInstruction={onSelectInstruction}
                  onPositionAction={
                    onGroupPositionAction
                      ? (position, mode) => onGroupPositionAction(g, position, mode)
                      : undefined
                  }
                  actionBusyKey={actionBusyKey}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type PositionsTab = 'groups' | 'synced' | 'history';

type PositionLegActionMode = 'add' | 'reduce';

type PositionLegSizeMode = 'token' | 'usd' | 'percent';

const DEFAULT_REDUCE_PERCENT = 20;

function formatTokenSizeInput(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0.01';
  const digits = n >= 1 ? 6 : 8;
  return String(Number(n.toPrecision(digits)));
}

function defaultSingleLegTokenSize(position: PositionInGroupView, mode: PositionLegActionMode): string {
  if (mode === 'reduce' && position.size > 0) {
    return formatTokenSizeInput(position.size * (DEFAULT_REDUCE_PERCENT / 100));
  }
  if (position.mark_price > 0) {
    return formatTokenSizeInput(200 / position.mark_price);
  }
  return '0.01';
}

function defaultSingleLegNotionalUsd(position: PositionInGroupView, mode: PositionLegActionMode): string {
  if (mode === 'reduce' && position.usd_value > 0) {
    return String(Math.max(1, Math.round(position.usd_value * (DEFAULT_REDUCE_PERCENT / 100))));
  }
  return '200';
}

function defaultPositionLegPercent(_position: PositionInGroupView, mode: PositionLegActionMode): string {
  return mode === 'reduce' ? String(DEFAULT_REDUCE_PERCENT) : '100';
}

function defaultPositionLegAmount(
  position: PositionInGroupView,
  mode: PositionLegActionMode,
  sizeMode: PositionLegSizeMode,
): string {
  if (sizeMode === 'percent') {
    return defaultPositionLegPercent(position, mode);
  }
  return sizeMode === 'usd'
    ? defaultSingleLegNotionalUsd(position, mode)
    : defaultSingleLegTokenSize(position, mode);
}

function positionLegAmountFromPercent(
  position: PositionInGroupView,
  percent: number,
): { tokenSize: number; notionalUsd: number } {
  const ratio = Math.min(100, Math.max(0, percent)) / 100;
  return {
    tokenSize: position.size > 0 ? position.size * ratio : 0,
    notionalUsd: position.usd_value > 0 ? position.usd_value * ratio : 0,
  };
}

function formatNotionalUsdInput(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '1';
  return String(Math.max(1, Math.round(n * 100) / 100));
}

function reduceAmountFromPercent(
  position: PositionInGroupView,
  percent: number,
): { percent: number; tokenSize: string; notionalUsd: string } {
  const clamped = Math.min(100, Math.max(0, percent));
  const { tokenSize, notionalUsd } = positionLegAmountFromPercent(position, clamped);
  return {
    percent: clamped,
    tokenSize: formatTokenSizeInput(tokenSize),
    notionalUsd: formatNotionalUsdInput(notionalUsd),
  };
}

function reduceAmountFromToken(
  position: PositionInGroupView,
  tokenRaw: string,
): { percent: number; tokenSize: string; notionalUsd: string } {
  const token = Number(tokenRaw.trim());
  if (!(token > 0) || !Number.isFinite(token)) {
    return { percent: 0, tokenSize: tokenRaw, notionalUsd: '' };
  }
  const ratio = position.size > 0 ? token / position.size : 0;
  const percent = Math.min(100, Math.max(0, ratio * 100));
  const notionalUsd =
    position.usd_value > 0 && position.size > 0
      ? (token / position.size) * position.usd_value
      : position.mark_price > 0
        ? token * position.mark_price
        : 0;
  return {
    percent,
    tokenSize: tokenRaw,
    notionalUsd: notionalUsd > 0 ? formatNotionalUsdInput(notionalUsd) : '',
  };
}

function reduceAmountFromUsd(
  position: PositionInGroupView,
  usdRaw: string,
): { percent: number; tokenSize: string; notionalUsd: string } {
  const usd = Number(usdRaw.trim());
  if (!(usd > 0) || !Number.isFinite(usd)) {
    return { percent: 0, tokenSize: '', notionalUsd: usdRaw };
  }
  const ratio = position.usd_value > 0 ? usd / position.usd_value : 0;
  const percent = Math.min(100, Math.max(0, ratio * 100));
  const tokenSize =
    position.usd_value > 0 && position.size > 0
      ? (usd / position.usd_value) * position.size
      : position.mark_price > 0
        ? usd / position.mark_price
        : 0;
  return {
    percent,
    tokenSize: tokenSize > 0 ? formatTokenSizeInput(tokenSize) : '',
    notionalUsd: usdRaw,
  };
}

type PositionLegSubmitBy = 'token' | 'usd';

export type PositionLegSubmitPayload = {
  submitBy: PositionLegSubmitBy;
  amount: number;
};

function PositionLegReduceForm({
  position,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  position: PositionInGroupView;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: PositionLegSubmitPayload) => void;
}) {
  const initial = useMemo(
    () => reduceAmountFromPercent(position, DEFAULT_REDUCE_PERCENT),
    [position],
  );
  const [percent, setPercent] = useState(initial.percent);
  const [tokenSize, setTokenSize] = useState(initial.tokenSize);
  const [notionalUsd, setNotionalUsd] = useState(initial.notionalUsd);
  const [submitBy, setSubmitBy] = useState<PositionLegSubmitBy>('token');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const next = reduceAmountFromPercent(position, DEFAULT_REDUCE_PERCENT);
    setPercent(next.percent);
    setTokenSize(next.tokenSize);
    setNotionalUsd(next.notionalUsd);
    setSubmitBy('token');
    setLocalError(null);
  }, [position.exchange, position.inst_id, position.side]);

  const applyPercent = (nextPercent: number) => {
    const next = reduceAmountFromPercent(position, nextPercent);
    setPercent(next.percent);
    setTokenSize(next.tokenSize);
    setNotionalUsd(next.notionalUsd);
    setSubmitBy('token');
    setLocalError(null);
  };

  const handleTokenChange = (value: string) => {
    const next = reduceAmountFromToken(position, value);
    setPercent(next.percent);
    setTokenSize(next.tokenSize);
    setNotionalUsd(next.notionalUsd);
    setSubmitBy('token');
    setLocalError(null);
  };

  const handleUsdChange = (value: string) => {
    const next = reduceAmountFromUsd(position, value);
    setPercent(next.percent);
    setTokenSize(next.tokenSize);
    setNotionalUsd(next.notionalUsd);
    setSubmitBy('usd');
    setLocalError(null);
  };

  const handleConfirm = () => {
    if (submitBy === 'usd') {
      const usd = Number(notionalUsd.trim());
      if (!(usd > 0) || !Number.isFinite(usd)) {
        setLocalError('USD 名义须为有效正数');
        return;
      }
      if (position.usd_value > 0 && usd > position.usd_value * 1.0001) {
        setLocalError('USD 名义不能超过当前仓位名义');
        return;
      }
      onSubmit({ submitBy: 'usd', amount: usd });
      return;
    }
    const token = Number(tokenSize.trim());
    if (!(token > 0) || !Number.isFinite(token)) {
      setLocalError('Token 数量须为有效正数');
      return;
    }
    if (position.size > 0 && token > position.size * 1.0001) {
      setLocalError('Token 数量不能超过当前仓位');
      return;
    }
    onSubmit({ submitBy: 'token', amount: token });
  };

  return (
    <>
      <label className="realtime-spread-pos-instruction-label">
        减仓比例
        <div className="realtime-spread-pos-reduce-percent-row">
          <input
            type="range"
            className="realtime-spread-pos-reduce-slider"
            min={0}
            max={100}
            step={1}
            value={percent}
            disabled={busy}
            onChange={(e) => applyPercent(Number(e.target.value))}
          />
          <input
            type="number"
            className="realtime-spread-pos-reduce-percent-input"
            min={0}
            max={100}
            step="any"
            value={Number.isFinite(percent) ? percent : 0}
            disabled={busy}
            onChange={(e) => applyPercent(Number(e.target.value))}
          />
          <span className="realtime-spread-pos-reduce-percent-suffix">%</span>
        </div>
      </label>
      <label className="realtime-spread-pos-instruction-label">
        Token 数量（非 contract size）
        <input
          type="number"
          min={0}
          step="any"
          value={tokenSize}
          disabled={busy}
          onChange={(e) => handleTokenChange(e.target.value)}
        />
      </label>
      <label className="realtime-spread-pos-instruction-label">
        名义 (USD)
        <input
          type="number"
          min={0}
          step="any"
          value={notionalUsd}
          disabled={busy}
          onChange={(e) => handleUsdChange(e.target.value)}
        />
      </label>
      <p className="realtime-spread-pos-action-hint">
        拖动比例条会同步更新 Token / USD；也可直接修改任一输入框，比例会自动反算。
      </p>
      {localError || error ? (
        <div className="realtime-spread-positions-error realtime-spread-pos-instruction-error">
          {localError ?? error}
        </div>
      ) : null}
      <div className="realtime-spread-pos-actions">
        <button type="button" className="realtime-spread-positions-btn" disabled={busy} onClick={onClose}>
          取消
        </button>
        <button
          type="button"
          className="realtime-spread-positions-btn primary realtime-spread-pos-instruction-submit"
          disabled={busy}
          onClick={handleConfirm}
        >
          {busy ? '提交中…' : '确认减仓'}
        </button>
      </div>
    </>
  );
}

function parsePositionSide(sideRaw: string): 'Long' | 'Short' | null {
  const normalized = sideRaw.trim().toLowerCase();
  if (normalized === 'long') return 'Long';
  if (normalized === 'short') return 'Short';
  return null;
}

function sortSyncedPositionRows(
  buckets: SyncedPositionsView[]
): Array<{ exchange: string; synced_at_ms: number; position: PositionInGroupView }> {
  const rows: Array<{ exchange: string; synced_at_ms: number; position: PositionInGroupView }> = [];
  for (const bucket of buckets) {
    for (const position of bucket.positions) {
      rows.push({
        exchange: bucket.exchange,
        synced_at_ms: bucket.synced_at_ms,
        position,
      });
    }
  }
  return rows.sort((a, b) => b.position.usd_value - a.position.usd_value);
}

function SyncedPositionsTable({
  buckets,
  onPositionAction,
  actionBusyKey,
}: {
  buckets: SyncedPositionsView[];
  onPositionAction: (
    row: { exchange: string; synced_at_ms: number; position: PositionInGroupView },
    mode: PositionLegActionMode
  ) => void;
  actionBusyKey: string | null;
}) {
  const rows = useMemo(() => sortSyncedPositionRows(buckets), [buckets]);
  return (
    <div className="realtime-spread-positions-table-wrap">
      <table className="realtime-spread-positions-table">
        <thead>
          <tr>
            <th>交易所</th>
            <th>同步时间 (UTC)</th>
            <th>Symbol</th>
            <th>inst_id</th>
            <th>方向</th>
            <th className="num">数量</th>
            <th className="num">Size</th>
            <th className="num">USD 价值</th>
            <th className="num">均价</th>
            <th className="num">标记价</th>
            <th className="num">涨跌幅</th>
            <th className="num">强平价</th>
            <th className="num">距强平</th>
            <th className="num">未实现盈亏</th>
            <th className="num">已实现盈亏</th>
            <th className="num">杠杆</th>
            <th className="num">资金费</th>
            <th>更新时间 (UTC)</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={19} className="realtime-spread-positions-empty">
                暂无同步仓位，等待 WS 推送或后台持仓同步。
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const { exchange, synced_at_ms, position: p } = row;
              const rowKey = `${exchange}-${p.inst_id}-${p.side}-${i}`;
              const busy = actionBusyKey === rowKey;
              const priceChange = positionPriceChangeRatio(p);
              const liqDist = liqDistanceRatio(p);
              return (
              <tr key={rowKey}>
                <td>{exchange}</td>
                <td className="mono small">{fmtTs(synced_at_ms)}</td>
                <td>{p.symbol}</td>
                <td className="mono">{p.inst_id}</td>
                <td className="realtime-spread-side-cell">
                  <span className={sideChipClass(p.side)}>{p.side}</span>
                </td>
                <td className="num">{fmtNum(p.quantity)}</td>
                <td className="num">{fmtNum(p.size)}</td>
                <td className="num">{fmtNum(p.usd_value, 2)}</td>
                <td className="num">{fmtNum(p.average_price)}</td>
                <td className="num">{fmtNum(p.mark_price)}</td>
                <td className={`num ${pnlToneClass(priceChange ?? 0)}`} title="(mark - avg) / avg，Short 方向取反">
                  {fmtPnlRatio(priceChange)}
                </td>
                <td className="num">{p.liq_price > 0 ? fmtNum(p.liq_price) : '—'}</td>
                <td
                  className={`num ${liqDist != null && liqDist < 0.3 ? 'is-loss' : ''}`}
                  title={
                    p.liq_price > 0
                      ? `|mark - liq| / mark = ${fmtLiqDistanceRatio(liqDist)}`
                      : undefined
                  }
                >
                  {fmtLiqDistanceRatio(liqDist)}
                </td>
                <td className="num">{fmtNum(p.unrealized_pnl, 2)}</td>
                <td className="num">{fmtNum(p.realized_pnl, 2)}</td>
                <td className="num">{fmtNum(p.leverage, 1)}</td>
                <td className="num">{fmtNum(p.funding_fee, 4)}</td>
                <td className="mono small">{fmtTs(p.last_update_time)}</td>
                <td className="realtime-spread-pos-actions">
                  <button
                    type="button"
                    className="realtime-spread-positions-btn"
                    disabled={busy || actionBusyKey != null}
                    onClick={() => onPositionAction(row, 'add')}
                  >
                    开仓
                  </button>
                  <button
                    type="button"
                    className="realtime-spread-positions-btn"
                    disabled={busy || actionBusyKey != null}
                    onClick={() => onPositionAction(row, 'reduce')}
                  >
                    减仓
                  </button>
                </td>
              </tr>
            );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

type PositionSingleLegDialogProps = {
  row: { exchange: string; position: PositionInGroupView };
  mode: PositionLegActionMode;
  sizeMode: PositionLegSizeMode;
  setSizeMode: (v: PositionLegSizeMode) => void;
  amount: string;
  setAmount: (v: string) => void;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmitAdd: () => void;
  onSubmitReduce: (payload: PositionLegSubmitPayload) => void;
};

function PositionSingleLegDialog({
  row,
  mode,
  sizeMode,
  setSizeMode,
  amount,
  setAmount,
  busy,
  error,
  onClose,
  onSubmitAdd,
  onSubmitReduce,
}: PositionSingleLegDialogProps) {
  const { offset, dragging, headerDragHandlers } = useDialogDrag();
  const p = row.position;
  const side = parsePositionSide(p.side);
  const isReduce = mode === 'reduce';
  const amountLabel = sizeMode === 'usd' ? '名义 (USD)' : 'Token 数量（非 contract size）';
  const amountPlaceholder = defaultPositionLegAmount(p, mode, sizeMode);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onSizeModeChange = (next: PositionLegSizeMode) => {
    setSizeMode(next);
    setAmount(defaultPositionLegAmount(p, mode, next));
  };

  return (
    <div
      className="realtime-spread-pos-modal-overlay realtime-spread-pos-action-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="realtime-spread-pos-modal realtime-spread-pos-action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="position-single-leg-title"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      >
        <header
          className={`realtime-spread-pos-modal-head realtime-spread-pos-action-modal-head${dragging ? ' is-dragging' : ''}`}
          {...headerDragHandlers}
        >
          <div>
            <h2 id="position-single-leg-title">
              {mode === 'add' ? '开仓' : '减仓'} · {row.exchange} · {p.inst_id}
            </h2>
            <p className="realtime-spread-pos-modal-sub">
              {side ?? p.side} · 当前 token {fmtNum(p.size)} · 名义 {fmtNum(p.usd_value, 2)} USD · 市价单腿 task
            </p>
          </div>
          <button type="button" className="realtime-spread-pos-modal-close" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="realtime-spread-pos-modal-body">
          <div className="realtime-spread-pos-instruction-form">
            {isReduce ? (
              <PositionLegReduceForm
                position={p}
                busy={busy}
                error={error}
                onClose={onClose}
                onSubmit={onSubmitReduce}
              />
            ) : (
              <>
                <label className="realtime-spread-pos-instruction-label">
                  数量单位
                  <select
                    value={sizeMode}
                    disabled={busy}
                    onChange={(e) => onSizeModeChange(e.target.value as PositionLegSizeMode)}
                  >
                    <option value="token">Token 数量</option>
                    <option value="usd">USD 名义</option>
                  </select>
                </label>
                <label className="realtime-spread-pos-instruction-label">
                  {amountLabel}
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={amount}
                    disabled={busy}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={amountPlaceholder}
                  />
                </label>
                {error ? (
                  <div className="realtime-spread-positions-error realtime-spread-pos-instruction-error">{error}</div>
                ) : null}
                <div className="realtime-spread-pos-actions">
                  <button type="button" className="realtime-spread-positions-btn" disabled={busy} onClick={onClose}>
                    取消
                  </button>
                  <button
                    type="button"
                    className="realtime-spread-positions-btn primary realtime-spread-pos-instruction-submit"
                    disabled={busy || side == null}
                    onClick={() => void onSubmitAdd()}
                  >
                    {busy ? '提交中…' : '确认开仓'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export type RealtimeSpreadPositionsProps = {
  selectedGroupId?: string | null;
  onSelectPositionGroup?: (g: PositionGroupView) => void;
  onChartLinkedGroupUpdate?: (g: PositionGroupView | null) => void;
  onPositionGroupsUpdate?: (groups: PositionGroupView[]) => void;
  symbol?: string;
  exchange1?: string;
  exchange2?: string;
  direction?: 'long1_short2' | 'short1_long2';
  latestOpenLineByDirection?: LatestOpenLineByDirection;
  /** 若提供，账户信息将通过 portal 渲染到该 DOM 节点（右侧栏底部）。 */
  accountSummaryHost?: HTMLElement | null;
};

/**
 * 实时页持仓：GET /position-groups，按 group 折叠展示，展开为组内 positions。
 * 默认与图表同列展示；账户信息可 portal 到右侧栏。
 */
export function RealtimeSpreadPositions({
  selectedGroupId = null,
  onSelectPositionGroup,
  onChartLinkedGroupUpdate,
  onPositionGroupsUpdate,
  symbol = '',
  exchange1 = '',
  exchange2 = '',
  direction = 'long1_short2',
  latestOpenLineByDirection,
  accountSummaryHost = null,
}: RealtimeSpreadPositionsProps) {
  const { user, isTradingSessionReady } = useAuth();
  const [activeTab, setActiveTab] = useState<PositionsTab>('groups');
  const [groups, setGroups] = useState<PositionGroupView[]>([]);
  const [syncedPositions, setSyncedPositions] = useState<SyncedPositionsView[]>([]);
  const [accounts, setAccounts] = useState<AccountInfoView[]>([]);
  const [dailyPnl, setDailyPnl] = useState<DailyAccountPnlView | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [autoRefreshEnabled] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState<PositionGroupView | null>(null);

  const { snapshot, connected, error: wsError } = usePortfolioWs(autoRefreshEnabled);

  useEffect(() => {
    if (!snapshot) return;
    setGroups(snapshot.position_groups);
    setSyncedPositions(snapshot.synced_positions);
    setAccounts(snapshot.accounts);
    setDailyPnl(snapshot.daily_pnl);
    setLastRefreshAt(snapshot.ts_ms);
    setLoadingGroups(false);
  }, [snapshot]);

  useEffect(() => {
    if (autoRefreshEnabled && connected && !snapshot) {
      setLoadingGroups(true);
    }
  }, [autoRefreshEnabled, connected, snapshot]);

  useEffect(() => {
    if (wsError) setError(wsError);
  }, [wsError]);

  useEffect(() => {
    if (!detailGroup) return;
    const latest = groups.find((g) => g.group_id === detailGroup.group_id);
    if (latest) setDetailGroup(latest);
  }, [groups, detailGroup?.group_id]);

  useEffect(() => {
    onPositionGroupsUpdate?.(groups);
  }, [groups, onPositionGroupsUpdate]);

  useEffect(() => {
    if (!onChartLinkedGroupUpdate || !selectedGroupId) return;
    const latest = groups.find((g) => g.group_id === selectedGroupId) ?? null;
    onChartLinkedGroupUpdate(latest);
  }, [groups, onChartLinkedGroupUpdate, selectedGroupId]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [modalExpandedIds, setModalExpandedIds] = useState<Set<string>>(() => new Set());

  const [error, setError] = useState<string | null>(null);

  /** 分组开仓 / 关仓表单默认值（与 ManualTradingPanel 一致） */
  const [groupOpenTotal, setGroupOpenTotal] = useState('200');
  const [groupOpenPer, setGroupOpenPer] = useState('20');
  const [groupOpenMode, setGroupOpenMode] =
    useState<OpenInstructionBody['threshold_mode']>('GreaterOrEqual');
  const [groupOpenThreshold, setGroupOpenThreshold] = useState('0.005');
  const [groupOpenMinIntervalMs, setGroupOpenMinIntervalMs] = useState('500');

  const [groupCloseValidity, setGroupCloseValidity] = useState<InstructionValidityView>('one_shot');
  const [groupCloseSizeMode, setGroupCloseSizeMode] = useState<'token' | 'usd'>('usd');
  const [groupCloseTargetAmount, setGroupCloseTargetAmount] = useState('100');
  const [groupClosePer, setGroupClosePer] = useState('20');
  const [groupCloseMode, setGroupCloseMode] = useState<'Immediate' | 'SpreadThreshold'>('Immediate');
  const [groupCloseThreshold, setGroupCloseThreshold] = useState('0');
  const [groupCloseMinIntervalMs, setGroupCloseMinIntervalMs] = useState('500');

  const [groupActionBusyId, setGroupActionBusyId] = useState<string | null>(null);
  const [instructionDialog, setInstructionDialog] = useState<{
    g: PositionGroupView;
    mode: GroupInstructionMode;
  } | null>(null);
  const [instructionDialogError, setInstructionDialogError] = useState<string | null>(null);
  const [selectedInstruction, setSelectedInstruction] = useState<InstructionView | null>(null);

  const openInstructionRecords = useCallback((row: InstructionView) => {
    setSelectedInstruction(row);
  }, []);

  const [positionLegDialog, setPositionLegDialog] = useState<{
    row: { exchange: string; position: PositionInGroupView };
    mode: PositionLegActionMode;
    rowKey: string;
  } | null>(null);
  const [positionLegSizeMode, setPositionLegSizeMode] = useState<PositionLegSizeMode>('token');
  const [positionLegAmount, setPositionLegAmount] = useState('0.01');
  const [positionLegDialogError, setPositionLegDialogError] = useState<string | null>(null);
  const [positionLegBusyKey, setPositionLegBusyKey] = useState<string | null>(null);
  const [activeInstructions, setActiveInstructions] = useState<InstructionView[]>([]);
  const [groupSettingsById, setGroupSettingsById] = useState<Map<string, boolean>>(
    () => new Map(),
  );
  const [autoBalanceBusyId, setAutoBalanceBusyId] = useState<string | null>(null);
  const instructionsMounted = useRef(true);

  const loadGroupSettings = useCallback(async () => {
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) return;
    try {
      const settings = await listPositionGroupSettings();
      if (!instructionsMounted.current) return;
      setGroupSettingsById(
        new Map(settings.map((row) => [row.group_id, row.auto_balance_enabled])),
      );
    } catch {
      if (!instructionsMounted.current) return;
    }
  }, [isTradingSessionReady, user]);

  const handleAutoBalanceChange = useCallback(
    async (g: PositionGroupView, enabled: boolean) => {
      const prevEnabled = groupSettingsById.get(g.group_id) ?? true;
      setGroupSettingsById((current) => {
        const next = new Map(current);
        next.set(g.group_id, enabled);
        return next;
      });
      setAutoBalanceBusyId(g.group_id);
      try {
        const updated = await updatePositionGroupSetting(g.group_id, enabled);
        setGroupSettingsById((current) => {
          const next = new Map(current);
          next.set(updated.group_id, updated.auto_balance_enabled);
          return next;
        });
      } catch (err) {
        setGroupSettingsById((current) => {
          const next = new Map(current);
          if (prevEnabled) {
            next.delete(g.group_id);
          } else {
            next.set(g.group_id, prevEnabled);
          }
          return next;
        });
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setAutoBalanceBusyId(null);
      }
    },
    [groupSettingsById],
  );

  const loadActiveInstructions = useCallback(async () => {
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) return;
    try {
      const rows = await listInstructionsIfIdle();
      if (rows === null || !instructionsMounted.current) return;
      setActiveInstructions(rows);
    } catch {
      if (!instructionsMounted.current) return;
      setActiveInstructions([]);
    }
  }, [isTradingSessionReady, user]);

  const instructionIndex = useMemo(
    () => indexActiveInstructionsByGroupKey(activeInstructions),
    [activeInstructions],
  );

  const groupIdsKey = useMemo(
    () =>
      groups
        .map((g) => g.group_id)
        .sort()
        .join('|'),
    [groups],
  );

  useEffect(() => {
    instructionsMounted.current = true;
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) {
      setActiveInstructions([]);
      return undefined;
    }

    const { run: scheduleLoad } = createSerialLoader(async () => {
      await loadActiveInstructions();
    });

    scheduleLoad();
    const timer = autoRefreshEnabled
      ? window.setInterval(() => scheduleLoad({ coalesce: true }), INSTRUCTION_POLL_MS)
      : null;
    const onInstructionsChanged = () => scheduleLoad({ coalesce: true });
    window.addEventListener(INSTRUCTIONS_CHANGED_EVENT, onInstructionsChanged);

    return () => {
      instructionsMounted.current = false;
      if (timer != null) window.clearInterval(timer);
      window.removeEventListener(INSTRUCTIONS_CHANGED_EVENT, onInstructionsChanged);
    };
  }, [autoRefreshEnabled, loadActiveInstructions, isTradingSessionReady, user]);

  useEffect(() => {
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) {
      setGroupSettingsById(new Map());
      return;
    }
    void loadGroupSettings();
  }, [groupIdsKey, isTradingSessionReady, loadGroupSettings, user]);

  const toggleGroup = useCallback((g: PositionGroupView) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(g.group_id)) next.delete(g.group_id);
      else next.add(g.group_id);
      return next;
    });
  }, []);

  const toggleModalGroup = useCallback((g: PositionGroupView) => {
    setModalExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(g.group_id)) next.delete(g.group_id);
      else next.add(g.group_id);
      return next;
    });
  }, []);

  const linkGroupChart = useCallback(
    (g: PositionGroupView) => {
      onSelectPositionGroup?.(g);
    },
    [onSelectPositionGroup],
  );

  const sortedGroups = useMemo(() => sortGroupsByUsdDesc(groups), [groups]);

  const openModal = useCallback(() => {
    setModalExpandedIds(new Set(sortedGroups.map((g) => g.group_id)));
    setModalOpen(true);
  }, [sortedGroups]);

  const submitGroupOpen = useCallback(async () => {
    const g = instructionDialog?.g;
    if (!g) return;
    const hp = hedgeParamsFromGroup(g);
    if ('error' in hp) {
      setInstructionDialogError(hp.error);
      return;
    }
    const total = Number(groupOpenTotal.trim());
    const perOrder = Number(groupOpenPer.trim());
    const minIv = Number(groupOpenMinIntervalMs.trim());
    if (!(total > 0) || !Number.isFinite(total)) {
      setInstructionDialogError('总名义须为有效正数');
      return;
    }
    if (!(perOrder > 0) || !Number.isFinite(perOrder)) {
      setInstructionDialogError('单次开仓名义须为有效正数');
      return;
    }
    let threshold: number | null = null;
    if (groupOpenMode !== 'Immediate') {
      threshold = Number(groupOpenThreshold.trim());
      if (!Number.isFinite(threshold)) {
        setInstructionDialogError('开仓价差阈值须为有效数字');
        return;
      }
      const spreadDirection = resolveSpreadDirectionForLegs(
        exchange1,
        exchange2,
        hp.long_exchange,
        hp.short_exchange,
      );
      const openLine =
        spreadDirection && latestOpenLineByDirection
          ? latestOpenLineByDirection[spreadDirection]
          : null;
      if (
        spreadDirection &&
        !confirmOpenThresholdIfNeeded({
          threshold,
          openLine,
          direction: spreadDirection,
        })
      ) {
        return;
      }
    }
    setGroupActionBusyId(g.group_id);
    setInstructionDialogError(null);
    setError(null);
    const dialogSnapshot = instructionDialog;
    setInstructionDialog(null);
    try {
      await createOpenInstruction({
        symbol: hp.symbol,
        long_inst_id: hp.long_inst_id,
        short_inst_id: hp.short_inst_id,
        long_exchange: hp.long_exchange,
        short_exchange: hp.short_exchange,
        target_notional: total,
        per_order_notional: perOrder,
        threshold_mode: groupOpenMode,
        threshold,
        min_order_interval_ms: Number.isFinite(minIv) && minIv >= 0 ? minIv : 0,
      });
      notifyInstructionsChanged();
    } catch (e) {
      setInstructionDialog(dialogSnapshot);
      setInstructionDialogError(e instanceof Error ? e.message : String(e));
    } finally {
      setGroupActionBusyId(null);
    }
  }, [
    instructionDialog,
    groupOpenTotal,
    groupOpenPer,
    groupOpenMode,
    groupOpenThreshold,
    groupOpenMinIntervalMs,
    exchange1,
    exchange2,
    latestOpenLineByDirection,
  ]);

  const submitGroupClose = useCallback(async () => {
    const g = instructionDialog?.g;
    if (!g) return;
    const hp = hedgeParamsFromGroup(g);
    if ('error' in hp) {
      setInstructionDialogError(hp.error);
      return;
    }
    const isPersistentClose = groupCloseValidity === 'persistent';
    const targetAmount = Number(groupCloseTargetAmount.trim());
    const perOrder = Number(groupClosePer.trim());
    const minIv = Number(groupCloseMinIntervalMs.trim());
    if (!isPersistentClose && (!(targetAmount > 0) || !Number.isFinite(targetAmount))) {
      setInstructionDialogError(
        groupCloseSizeMode === 'usd' ? '总关仓 USD 名义须为正数' : '总关仓 token 数量须为正数',
      );
      return;
    }
    if (!(perOrder > 0)) {
      setInstructionDialogError('单次关仓名义须为正数');
      return;
    }
    let threshold: number | null = null;
    if (groupCloseMode === 'SpreadThreshold') {
      threshold = Number(groupCloseThreshold.trim());
      if (!Number.isFinite(threshold)) {
        setInstructionDialogError('关仓价差阈值须为有效数字');
        return;
      }
    }
    setGroupActionBusyId(g.group_id);
    setInstructionDialogError(null);
    setError(null);
    const dialogSnapshot = instructionDialog;
    setInstructionDialog(null);
    try {
      await createCloseInstruction({
        symbol: hp.symbol,
        long_inst_id: hp.long_inst_id,
        short_inst_id: hp.short_inst_id,
        long_exchange: hp.long_exchange,
        short_exchange: hp.short_exchange,
        ...(isPersistentClose
          ? {}
          : groupCloseSizeMode === 'usd'
            ? { target_notional: targetAmount }
            : { target_size: targetAmount }),
        per_order_notional: perOrder,
        threshold_mode: closeThresholdMode(groupCloseMode),
        threshold,
        min_order_interval_ms: Number.isFinite(minIv) && minIv >= 0 ? minIv : 0,
        validity: groupCloseValidity,
      });
      notifyInstructionsChanged();
    } catch (e) {
      setInstructionDialog(dialogSnapshot);
      setInstructionDialogError(e instanceof Error ? e.message : String(e));
    } finally {
      setGroupActionBusyId(null);
    }
  }, [
    instructionDialog,
    groupCloseValidity,
    groupCloseTargetAmount,
    groupCloseSizeMode,
    groupClosePer,
    groupCloseMode,
    groupCloseThreshold,
    groupCloseMinIntervalMs,
  ]);

  const openPositionLegAction = useCallback(
    (
      exchange: string,
      position: PositionInGroupView,
      mode: PositionLegActionMode,
      rowKey: string,
    ) => {
      setPositionLegDialogError(null);
      setPositionLegSizeMode('token');
      setPositionLegAmount(defaultPositionLegAmount(position, mode, 'token'));
      setPositionLegDialog({ row: { exchange, position }, mode, rowKey });
    },
    [],
  );

  const onGroupPositionAction = useCallback(
    (g: PositionGroupView, position: PositionInGroupView, mode: PositionLegActionMode) => {
      openPositionLegAction(
        position.exchange,
        position,
        mode,
        positionLegRowKey(position.exchange, position, g.group_id),
      );
    },
    [openPositionLegAction],
  );

  const submitPositionLegAdd = useCallback(async () => {
    if (!positionLegDialog || positionLegDialog.mode !== 'add') return;
    const position = positionLegDialog.row.position;
    const side = parsePositionSide(position.side);
    if (!side) {
      setPositionLegDialogError(`无法识别仓位方向：${position.side}`);
      return;
    }
    const rawAmount = Number(positionLegAmount.trim());
    if (!(rawAmount > 0) || !Number.isFinite(rawAmount)) {
      setPositionLegDialogError(
        positionLegSizeMode === 'usd' ? 'USD 名义须为有效正数' : 'Token 数量须为有效正数',
      );
      return;
    }
    const body: SubmitSingleLegTaskBody = {
      exchange: positionLegDialog.row.exchange,
      symbol: position.symbol,
      inst_id: position.inst_id,
      action: 'Open',
      position_side: side,
      ...(positionLegSizeMode === 'usd' ? { notional: rawAmount } : { token_size: rawAmount }),
    };
    const dialogSnapshot = positionLegDialog;
    setPositionLegDialog(null);
    setPositionLegBusyKey(dialogSnapshot.rowKey);
    setPositionLegDialogError(null);
    try {
      await submitSingleLegTask(body);
    } catch (e) {
      setPositionLegDialog(dialogSnapshot);
      setPositionLegDialogError(e instanceof Error ? e.message : String(e));
    } finally {
      setPositionLegBusyKey(null);
    }
  }, [positionLegDialog, positionLegAmount, positionLegSizeMode]);

  const submitPositionLegReduce = useCallback(
    async (payload: PositionLegSubmitPayload) => {
      if (!positionLegDialog || positionLegDialog.mode !== 'reduce') return;
      const position = positionLegDialog.row.position;
      const side = parsePositionSide(position.side);
      if (!side) {
        setPositionLegDialogError(`无法识别仓位方向：${position.side}`);
        return;
      }
      const body: SubmitSingleLegTaskBody = {
        exchange: positionLegDialog.row.exchange,
        symbol: position.symbol,
        inst_id: position.inst_id,
        action: 'Close',
        position_side: side,
        ...(payload.submitBy === 'usd'
          ? { notional: payload.amount }
          : { token_size: payload.amount }),
      };
      const dialogSnapshot = positionLegDialog;
      setPositionLegDialog(null);
      setPositionLegBusyKey(dialogSnapshot.rowKey);
      setPositionLegDialogError(null);
      try {
        await submitSingleLegTask(body);
      } catch (e) {
        setPositionLegDialog(dialogSnapshot);
        setPositionLegDialogError(e instanceof Error ? e.message : String(e));
      } finally {
        setPositionLegBusyKey(null);
      }
    },
    [positionLegDialog],
  );

  const openGroupInstruction = useCallback((g: PositionGroupView, mode: GroupInstructionMode) => {
    setInstructionDialogError(null);
    setInstructionDialog({ g, mode });
  }, []);

  const openGroupDetail = useCallback((g: PositionGroupView) => {
    setDetailGroup(g);
  }, []);

  const accountSummaryNode: ReactNode = (
    <PortfolioHeaderSummary
      groups={sortedGroups}
      syncedPositions={syncedPositions}
      accounts={accounts}
      dailyPnl={dailyPnl}
      variant={accountSummaryHost ? 'sidebar' : 'inline'}
    />
  );

  if (!isManualTradingConfigured()) {
    return (
      <section className="realtime-spread-positions" aria-label="持仓">
        <div className="realtime-spread-positions-head">
          <h2>持仓</h2>
        </div>
        <p className="realtime-spread-positions-hint">
          配置 <code>VITE_MANUAL_TRADING_ORIGIN</code> 并登录后，可在此查看 trading-engine 持仓分组。
        </p>
      </section>
    );
  }

  return (
    <section
      className={`realtime-spread-positions${accountSummaryHost ? ' is-chart-aligned' : ''}`}
      aria-label="持仓"
    >
      {accountSummaryHost ? createPortal(accountSummaryNode, accountSummaryHost) : null}
      <div
        className={`realtime-spread-positions-head${accountSummaryHost ? ' is-without-account' : ''}`}
      >
        <div className="realtime-spread-positions-head-left">
          <div className="realtime-spread-positions-head-title">
            <h2>持仓</h2>
            <span
              className="realtime-spread-positions-live"
              title="WebSocket 每秒推送持仓与账户快照"
            >
              WS 推送
              {lastRefreshAt != null ? (
                <span className="mono small"> · {fmtTs(lastRefreshAt)}</span>
              ) : null}
            </span>
          </div>
          <div className="realtime-spread-positions-tabs" role="tablist" aria-label="持仓视图">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'groups'}
              className={activeTab === 'groups' ? 'is-active' : undefined}
              onClick={() => setActiveTab('groups')}
            >
              Position Groups
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'synced'}
              className={activeTab === 'synced' ? 'is-active' : undefined}
              onClick={() => setActiveTab('synced')}
            >
              全部仓位
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'history'}
              className={activeTab === 'history' ? 'is-active' : undefined}
              onClick={() => setActiveTab('history')}
            >
              历史成交
            </button>
          </div>
        </div>
        {!accountSummaryHost ? accountSummaryNode : null}
        <div className="realtime-spread-positions-actions">
          <button
            type="button"
            className="realtime-spread-positions-btn icon"
            aria-label="放大查看 Position Groups"
            title="放大查看"
            onClick={() => openModal()}
          >
            <ExpandIcon />
          </button>
        </div>
      </div>
      {error ? <div className="realtime-spread-positions-error">{error}</div> : null}

      {modalOpen ? (
        <PositionGroupsModal
          groups={sortedGroups}
          instructionIndex={instructionIndex}
          lastRefreshAt={lastRefreshAt}
          autoRefreshEnabled={autoRefreshEnabled}
          error={error}
          expandedIds={modalExpandedIds}
          selectedGroupId={selectedGroupId}
          onToggleGroup={toggleModalGroup}
          onLinkChart={linkGroupChart}
          onClose={() => setModalOpen(false)}
          onShowDetail={openGroupDetail}
          onOpenAction={(g) => openGroupInstruction(g, 'open')}
          onCloseAction={(g) => openGroupInstruction(g, 'close')}
          onSelectInstruction={openInstructionRecords}
          onGroupPositionAction={onGroupPositionAction}
          actionBusyKey={positionLegBusyKey}
          groupSettingsById={groupSettingsById}
          autoBalanceBusyId={autoBalanceBusyId}
          onAutoBalanceChange={(g, enabled) => void handleAutoBalanceChange(g, enabled)}
        />
      ) : null}

      {detailGroup ? (
        <PositionGroupDetailModal
          g={detailGroup}
          activeInstructions={activeInstructionsForGroup(detailGroup, instructionIndex)}
          onSelectInstruction={openInstructionRecords}
          onPositionAction={(position, mode) => onGroupPositionAction(detailGroup, position, mode)}
          actionBusyKey={positionLegBusyKey}
          onClose={() => setDetailGroup(null)}
        />
      ) : null}

      {selectedInstruction ? (
        <InstructionExecutionDialog
          instruction={selectedInstruction}
          onClose={() => setSelectedInstruction(null)}
        />
      ) : null}

      {instructionDialog ? (
        <PositionGroupInstructionDialog
          g={instructionDialog.g}
          mode={instructionDialog.mode}
          onClose={() => {
            setInstructionDialog(null);
            setInstructionDialogError(null);
          }}
          dialogError={instructionDialogError}
          busy={groupActionBusyId === instructionDialog.g.group_id}
          anyBusy={groupActionBusyId !== null}
          onSubmitOpen={() => void submitGroupOpen()}
          onSubmitClose={() => void submitGroupClose()}
          openForm={{
            totalNotional: groupOpenTotal,
            setTotalNotional: setGroupOpenTotal,
            perOrderNotional: groupOpenPer,
            setPerOrderNotional: setGroupOpenPer,
            thresholdMode: groupOpenMode,
            setThresholdMode: setGroupOpenMode,
            spreadThreshold: groupOpenThreshold,
            setSpreadThreshold: setGroupOpenThreshold,
            minIntervalMs: groupOpenMinIntervalMs,
            setMinIntervalMs: setGroupOpenMinIntervalMs,
          }}
          closeForm={{
            validity: groupCloseValidity,
            setValidity: setGroupCloseValidity,
            sizeMode: groupCloseSizeMode,
            setSizeMode: setGroupCloseSizeMode,
            targetAmount: groupCloseTargetAmount,
            setTargetAmount: setGroupCloseTargetAmount,
            perOrderNotional: groupClosePer,
            setPerOrderNotional: setGroupClosePer,
            closeMode: groupCloseMode,
            setCloseMode: setGroupCloseMode,
            spreadThreshold: groupCloseThreshold,
            setSpreadThreshold: setGroupCloseThreshold,
            minIntervalMs: groupCloseMinIntervalMs,
            setMinIntervalMs: setGroupCloseMinIntervalMs,
          }}
        />
      ) : null}

      {positionLegDialog ? (
        <PositionSingleLegDialog
          row={{ exchange: positionLegDialog.row.exchange, position: positionLegDialog.row.position }}
          mode={positionLegDialog.mode}
          sizeMode={positionLegSizeMode}
          setSizeMode={setPositionLegSizeMode}
          amount={positionLegAmount}
          setAmount={setPositionLegAmount}
          busy={positionLegBusyKey === positionLegDialog.rowKey}
          error={positionLegDialogError}
          onClose={() => {
            setPositionLegDialog(null);
            setPositionLegDialogError(null);
          }}
          onSubmitAdd={() => void submitPositionLegAdd()}
          onSubmitReduce={(payload) => void submitPositionLegReduce(payload)}
        />
      ) : null}

      <div className="realtime-spread-pos-groups">
        {activeTab === 'history' ? (
          <PairHistoryTasksPanel
            symbol={symbol}
            exchange1={exchange1}
            exchange2={exchange2}
            realtimeDirection={direction}
            enabled={activeTab === 'history'}
          />
        ) : activeTab === 'synced' ? (
          <SyncedPositionsTable
            buckets={syncedPositions}
            actionBusyKey={positionLegBusyKey}
            onPositionAction={(row, mode) => {
              openPositionLegAction(
                row.exchange,
                row.position,
                mode,
                positionLegRowKey(row.exchange, row.position),
              );
            }}
          />
        ) : sortedGroups.length === 0 && !loadingGroups ? (
          <p className="realtime-spread-positions-empty">
            暂无 position group；可先点「从交易所同步」，或开仓执行后可能出现分组。
          </p>
        ) : (
          sortedGroups.map((g) => (
            <PositionGroupCard
              key={g.group_id}
              g={g}
              expanded={expandedIds.has(g.group_id)}
              selected={selectedGroupId === g.group_id}
              activeInstructions={activeInstructionsForGroup(g, instructionIndex)}
              autoBalanceEnabled={groupSettingsById.get(g.group_id) ?? true}
              autoBalanceBusy={autoBalanceBusyId === g.group_id}
              onAutoBalanceChange={(enabled) => void handleAutoBalanceChange(g, enabled)}
              onToggle={() => toggleGroup(g)}
              onLinkChart={linkGroupChart}
              onShowDetail={openGroupDetail}
              onOpenAction={(g) => openGroupInstruction(g, 'open')}
              onCloseAction={(g) => openGroupInstruction(g, 'close')}
              onSelectInstruction={openInstructionRecords}
              onPositionAction={(position, mode) => onGroupPositionAction(g, position, mode)}
              actionBusyKey={positionLegBusyKey}
            />
          ))
        )}
      </div>
    </section>
  );
}
